import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EditOutlined, EyeOutlined, FileOutlined, HistoryOutlined, PrinterOutlined, DeleteOutlined } from '@ant-design/icons';
import { Col, Descriptions, FloatButton, Row, Tag, Timeline, Table, Button, Modal, Space, Popconfirm } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from "react-toastify";
import { useReactToPrint } from 'react-to-print';
import { ContentHeader } from "../../components";
import { urlAdmin } from "../../constants";
import { Links } from "../../helpers/Links";
import { menuHelper } from "../../helpers/account";
import { dateCommon, formatVND } from "../../helpers/common";
import { ChiTietTaiSan, TaiSan, TrangThaiTaiSan } from "../../models/taisan/TaiSan";
import LoadingDetailPage from "../../modules/loading/LoadingDetailPage";
import { layChiTietTaiSan, xoaTaiSanPhu } from "../../services/taisan";

const XemTaiSan = () => {
    const navigate = useNavigate();
    // Lấy id từ query string
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [taiSan, setTaiSan] = useState<ChiTietTaiSan | null>(null);
    const menu: any = menuHelper.checkMultiPermission([Links.TaiSan.ChiTiet]);
    const componentRef = useRef<any>();
    const componentRefPhu = useRef<any>();

    // Modal xem chi tiết tài sản phụ
    const [showModalXemPhu, setShowModalXemPhu] = useState<boolean>(false);
    const [taiSanPhuDangXem, setTaiSanPhuDangXem] = useState<TaiSan | null>(null);

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `Tem_QR_${taiSan?.ma}_${new Date().getTime()}`,
    });

    const handlePrintPhu = useReactToPrint({
        content: () => componentRefPhu.current,
        documentTitle: `Tem_QR_phu_${taiSanPhuDangXem?.ma}_${new Date().getTime()}`,
    });

    const getTrangThaiTag = (trangThai: number) => {
        switch (trangThai) {
            case TrangThaiTaiSan.MoiNhap:
                return <Tag color="blue">Mới nhập</Tag>;
            case TrangThaiTaiSan.DangSuDung:
                return <Tag color="green">Đang sử dụng</Tag>;
            case TrangThaiTaiSan.BaoTri:
                return <Tag color="orange">Bảo trì</Tag>;
            case TrangThaiTaiSan.Hong:
                return <Tag color="red">Hỏng</Tag>;
            case TrangThaiTaiSan.ChoThanhLy:
                return <Tag color="purple">Chờ thanh lý</Tag>;
            case TrangThaiTaiSan.DieuChuyen:
                return <Tag color="cyan">Điều chuyển</Tag>;
            case TrangThaiTaiSan.DaThanhLy:
                return <Tag color="red">Đã thanh lý</Tag>;
            default:
                return <Tag>Không xác định</Tag>;
        }
    };

    const loadChiTiet = async () => {
        if (!id) return;

        try {
            setIsLoading(true);
            const result = await layChiTietTaiSan(parseInt(id));
            setTaiSan(result);
        } catch (error: any) {
            toast.error('Lỗi khi tải chi tiết tài sản: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleXemTaiSanPhu = (record: TaiSan) => {
        setTaiSanPhuDangXem(record);
        setShowModalXemPhu(true);
    };

    const columnsTaiSanPhu = [
        {
            title: 'Mã tài sản',
            key: 'ma',
            width: 140,
            render: (_: any, record: any) => (
                <div>
                    <div><strong>HRM:</strong> {record.ma}</div>
                    {record.maKeToan && (
                        <div style={{ fontSize: '11px', color: '#1890ff' }}>
                            <strong>FAST:</strong> {record.maKeToan}
                        </div>
                    )}
                </div>
            ),
        },
        { title: 'Tên tài sản', dataIndex: 'tenTaiSan', key: 'tenTaiSan' },
        { title: 'Bộ phận sử dụng', dataIndex: 'tenDonVi', key: 'tenDonVi' },
        { title: 'Vị trí SD', dataIndex: 'viTriSuDung', key: 'viTriSuDung' },
        { title: 'Loại', dataIndex: 'loaiTaiSan', key: 'loaiTaiSan', width: 140 },
        { title: 'SL', dataIndex: 'soLuong', key: 'soLuong', width: 55, align: 'center' as const },
        {
            title: 'Trạng thái', dataIndex: 'trangThai', key: 'trangThai', width: 130,
            render: (v: number) => getTrangThaiTag(v),
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 90,
            align: 'center' as 'center',
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleXemTaiSanPhu(record)}
                    />
                    <Popconfirm
                        title="Bạn có chắc muốn xóa?"
                        description="Tài sản phụ sẽ không còn là tài sản phụ"
                        onConfirm={async () => {
                            try {
                                await xoaTaiSanPhu(record.id);
                                toast.success('Xóa tài sản phụ thành công');
                                loadChiTiet();
                            } catch (error: any) {
                                toast.error('Lỗi khi xóa tài sản phụ: ' + error.message);
                            }
                        }}
                        okText="Đồng ý"
                        cancelText="Hủy"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />} size="small" />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        loadChiTiet();
    }, [id]);

    if (isLoading) {
        return <LoadingDetailPage title="Chi tiết tài sản" />;
    }

    return (
        <section style={{ paddingBottom: "52px" }}>
            <ContentHeader title="Chi tiết tài sản" />
            <Row>
                <Col span={24}>
                    {taiSan && (
                        <div className="card">
                            <div className="card-body">
                                <Row gutter={[16, 16]}>
                                    <Col span={24}>
                                        <h5 className="mb-3">Thông tin cơ bản</h5>
                                        <Descriptions bordered column={2}>
                                            <Descriptions.Item label="Tên tài sản">{taiSan.tenTaiSan}</Descriptions.Item>
                                            <Descriptions.Item label="Mã tài sản (HRM)">{taiSan.ma}</Descriptions.Item>
                                            <Descriptions.Item label="Mã FAST">{taiSan.maKeToan || '-'}</Descriptions.Item>
                                            <Descriptions.Item label="Loại tài sản">{taiSan.loaiTaiSan}</Descriptions.Item>
                                            <Descriptions.Item label="Cơ sở">{taiSan.tenCoSo || '-'}</Descriptions.Item>
                                            <Descriptions.Item label="Vị trí">{taiSan.tenBoPhan || '-'}</Descriptions.Item>
                                            <Descriptions.Item label="Số lượng">{taiSan.soLuong}</Descriptions.Item>
                                            <Descriptions.Item label="Bộ phận sử dụng">{taiSan.tenDonVi}</Descriptions.Item>
                                            <Descriptions.Item label="Nhân viên sử dụng">{taiSan.nguoiSuDung || '-'}</Descriptions.Item>
                                            <Descriptions.Item label="Vị trí sử dụng">{taiSan.viTriSuDung || '-'}</Descriptions.Item>
                                            <Descriptions.Item label="Trạng thái">{getTrangThaiTag(taiSan.trangThai)}</Descriptions.Item>
                                            <Descriptions.Item label="Ngày tăng (Kế toán)">
                                                {taiSan.ngayTang ? dateCommon.formatDate(taiSan.ngayTang) : '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Ngày bố trí">
                                                {taiSan.ngayNhapKho ? dateCommon.formatDate(taiSan.ngayNhapKho) : '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Ngày đưa vào sử dụng">
                                                {taiSan.ngayDuaVaoSuDung ? dateCommon.formatDate(taiSan.ngayDuaVaoSuDung) : '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Số kỳ phân bổ">
                                                {taiSan.soKyPhanBo ? `${taiSan.soKyPhanBo} tháng` : (taiSan.soNamPhanBo ? `${taiSan.soNamPhanBo} năm` : '-')}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Thời gian bảo hành">
                                                {taiSan.thoiGianBaoHanh ? `${taiSan.thoiGianBaoHanh} tháng` : '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Số Serial/Model">{taiSan.soSerial || '-'}</Descriptions.Item>
                                            <Descriptions.Item label="Nhà cung cấp">{taiSan.nhaCungCap || '-'}</Descriptions.Item>
                                            <Descriptions.Item label="Nguyên giá">
                                                {(taiSan.nguyenGia ?? taiSan.giaTriKhiNhap) ? formatVND(taiSan.nguyenGia ?? taiSan.giaTriKhiNhap) : '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Đã phân bổ / Hao mòn">
                                                {taiSan.giaTriDaPhanBo ? formatVND(taiSan.giaTriDaPhanBo) : '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Giá trị còn lại">
                                                {taiSan.giaTriConLai ? formatVND(taiSan.giaTriConLai) : '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Mã QR">
                                                {taiSan.maQR ? (
                                                    <div>
                                                        <QRCodeSVG
                                                            value={taiSan.maQR}
                                                            size={150}
                                                            level="M"
                                                            style={{ display: 'block', marginBottom: '8px' }}
                                                        />
                                                        <span style={{ fontSize: '12px' }}>{taiSan.maQR}</span>
                                                    </div>
                                                ) : '-'}
                                            </Descriptions.Item>
                                            <Descriptions.Item label="Mô tả" span={2}>
                                                {taiSan.moTa || '-'}
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Col>

                                    {taiSan.dsTaiLieu && taiSan.dsTaiLieu.length > 0 && (
                                        <Col span={24}>
                                            <h5 className="mt-3 mb-3"><FileOutlined /> Tài liệu đính kèm</h5>
                                            <ul>
                                                {taiSan.dsTaiLieu.map((file) => (
                                                    <li key={file.idTaiLieu}>
                                                        <a
                                                            href={urlAdmin + file.duongDan.replace(/\\/g, "/")}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {file.tenTep}
                                                        </a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </Col>
                                    )}

                                    {!taiSan.idTaiSanChinh && (
                                        <Col span={24}>
                                            <h5 className="mt-3 mb-3">Tài sản phụ</h5>
                                            {taiSan.dsTaiSanPhu && taiSan.dsTaiSanPhu.length > 0 ? (
                                                <Table
                                                    columns={columnsTaiSanPhu}
                                                    dataSource={taiSan.dsTaiSanPhu}
                                                    rowKey={(record) => record.id!}
                                                    pagination={false}
                                                    size="small"
                                                />
                                            ) : (
                                                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                                    <p>Chưa có tài sản phụ</p>
                                                </div>
                                            )}
                                        </Col>
                                    )}

                                    {taiSan.dsLichSu && taiSan.dsLichSu.length > 0 && (
                                        <Col span={24}>
                                            <h5 className="mt-3 mb-3"><HistoryOutlined /> Lịch sử sử dụng</h5>
                                            <Timeline
                                                items={taiSan.dsLichSu.map((ls) => ({
                                                    children: (
                                                        <>
                                                            <p style={{ marginBottom: 4 }}>
                                                                <strong>{ls.tenLoaiThayDoi}</strong> - {dateCommon.formatDate(ls.ngayThayDoi)}
                                                            </p>
                                                            <p style={{ marginBottom: 4 }}>Người thực hiện: {ls.nguoiThucHien}</p>
                                                            {ls.nguoiNhan && <p style={{ marginBottom: 4 }}>Người nhận: {ls.nguoiNhan}</p>}
                                                            <p style={{ marginBottom: 0 }}>Nội dung: {ls.noiDung}</p>
                                                        </>
                                                    ),
                                                }))}
                                            />
                                        </Col>
                                    )}
                                </Row>
                            </div>
                        </div>
                    )}

                    {/* Hidden printable template */}
                    <div style={{ display: 'none' }}>
                        <div ref={componentRef} style={{ padding: '20px' }}>
                            {taiSan && (
                                <div style={{ textAlign: 'center', border: '1px solid #ddd', padding: '20px', maxWidth: '400px', margin: '0 auto' }}>
                                    <h3 style={{ marginBottom: '15px' }}>{taiSan.tenTaiSan}</h3>
                                    <QRCodeSVG
                                        value={taiSan.maQR || ''}
                                        size={300}
                                        level="M"
                                        style={{ maxWidth: '100%' }}
                                    />
                                    <div style={{ marginTop: '15px', fontSize: '14px' }}>
                                        <p><strong>Mã tài sản:</strong> {taiSan.ma}</p>
                                        {taiSan.maKeToan && <p><strong>Mã FAST:</strong> {taiSan.maKeToan}</p>}
                                        <p><strong>Bộ phận sử dụng:</strong> {taiSan.tenDonVi}</p>
                                        {taiSan.viTriSuDung && <p><strong>Vị trí:</strong> {taiSan.viTriSuDung}</p>}
                                        <p><strong>Mã QR:</strong> {taiSan.maQR}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Hidden print template tài sản phụ */}
                    <div style={{ display: 'none' }}>
                        <div ref={componentRefPhu} style={{ padding: '20px' }}>
                            {taiSanPhuDangXem && (
                                <div style={{ textAlign: 'center', border: '1px solid #ddd', padding: '20px', maxWidth: '400px', margin: '0 auto' }}>
                                    <h3 style={{ marginBottom: '15px' }}>{taiSanPhuDangXem.tenTaiSan}</h3>
                                    <QRCodeSVG
                                        value={taiSanPhuDangXem.maQR || ''}
                                        size={300}
                                        level="M"
                                        style={{ maxWidth: '100%' }}
                                    />
                                    <div style={{ marginTop: '15px', fontSize: '14px' }}>
                                        <p><strong>Mã tài sản:</strong> {taiSanPhuDangXem.ma}</p>
                                        {taiSanPhuDangXem.maKeToan && <p><strong>Mã FAST:</strong> {taiSanPhuDangXem.maKeToan}</p>}
                                        <p><strong>Bộ phận sử dụng:</strong> {taiSanPhuDangXem.tenDonVi}</p>
                                        {taiSanPhuDangXem.viTriSuDung && <p><strong>Vị trí:</strong> {taiSanPhuDangXem.viTriSuDung}</p>}
                                        <p><strong>Mã QR:</strong> {taiSanPhuDangXem.maQR}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {taiSan && (
                        <FloatButton.Group
                            trigger="hover"
                            type="primary"
                            style={{ right: 50, bottom: 80 }}
                        >
                            {menu[Links.TaiSan.ChiTiet] && (
                                <FloatButton
                                    tooltip={<div>Chỉnh sửa thông tin tài sản</div>}
                                    className="hrm-btn-warning"
                                    icon={<EditOutlined />}
                                    onClick={() => navigate(`${Links.TaiSan.ChiTiet}?id=${id}`)}
                                />
                            )}
                            <FloatButton
                                tooltip={<div>In tem QR</div>}
                                className="hrm-btn-print"
                                icon={<PrinterOutlined />}
                                onClick={handlePrint}
                            />
                        </FloatButton.Group>
                    )}
                </Col>
            </Row>

            {/* Modal xem chi tiết tài sản phụ */}
            <Modal
                title="Chi tiết tài sản phụ"
                open={showModalXemPhu}
                onCancel={() => setShowModalXemPhu(false)}
                footer={[
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handlePrintPhu}>
                        In QR
                    </Button>,
                    <Button key="close" onClick={() => setShowModalXemPhu(false)}>
                        Đóng
                    </Button>,
                ]}
                width={850}
            >
                {taiSanPhuDangXem && (
                    <Descriptions bordered column={2} size="small">
                        <Descriptions.Item label="Tên tài sản">{taiSanPhuDangXem.tenTaiSan}</Descriptions.Item>
                        <Descriptions.Item label="Mã tài sản (HRM)">{taiSanPhuDangXem.ma}</Descriptions.Item>
                        <Descriptions.Item label="Mã FAST">{taiSanPhuDangXem.maKeToan || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Loại tài sản">{taiSanPhuDangXem.loaiTaiSan || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Cơ sở">{taiSanPhuDangXem.tenCoSo || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Vị trí">{taiSanPhuDangXem.tenBoPhan || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Số lượng">{taiSanPhuDangXem.soLuong}</Descriptions.Item>
                        <Descriptions.Item label="Bộ phận sử dụng">{taiSanPhuDangXem.tenDonVi || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Nhân viên sử dụng">{taiSanPhuDangXem.nguoiSuDung || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Vị trí sử dụng">{taiSanPhuDangXem.viTriSuDung || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Trạng thái">{getTrangThaiTag(taiSanPhuDangXem.trangThai)}</Descriptions.Item>
                        <Descriptions.Item label="Ngày tăng (Kế toán)">
                            {taiSanPhuDangXem.ngayTang ? dateCommon.formatDate(taiSanPhuDangXem.ngayTang) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ngày đưa vào sử dụng">
                            {taiSanPhuDangXem.ngayDuaVaoSuDung ? dateCommon.formatDate(taiSanPhuDangXem.ngayDuaVaoSuDung) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ngày bố trí">
                            {taiSanPhuDangXem.ngayNhapKho ? dateCommon.formatDate(taiSanPhuDangXem.ngayNhapKho) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Số kỳ phân bổ">
                            {taiSanPhuDangXem.soKyPhanBo ? `${taiSanPhuDangXem.soKyPhanBo} tháng` : (taiSanPhuDangXem.soNamPhanBo ? `${taiSanPhuDangXem.soNamPhanBo} năm` : '-')}
                        </Descriptions.Item>
                        <Descriptions.Item label="Thời gian bảo hành">
                            {taiSanPhuDangXem.thoiGianBaoHanh ? `${taiSanPhuDangXem.thoiGianBaoHanh} tháng` : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Số Serial/Model">{taiSanPhuDangXem.soSerial || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Nhà cung cấp">{taiSanPhuDangXem.nhaCungCap || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Nguyên giá">
                            {(taiSanPhuDangXem.nguyenGia ?? taiSanPhuDangXem.giaTriKhiNhap) ? formatVND(taiSanPhuDangXem.nguyenGia ?? taiSanPhuDangXem.giaTriKhiNhap) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Đã phân bổ / Hao mòn">
                            {taiSanPhuDangXem.giaTriDaPhanBo ? formatVND(taiSanPhuDangXem.giaTriDaPhanBo) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Giá trị còn lại">
                            {taiSanPhuDangXem.giaTriConLai ? formatVND(taiSanPhuDangXem.giaTriConLai) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Mã QR">
                            {taiSanPhuDangXem.maQR ? (
                                <div>
                                    <QRCodeSVG value={taiSanPhuDangXem.maQR} size={100} level="M"
                                        style={{ display: 'block', marginBottom: '4px' }} />
                                    <span style={{ fontSize: '11px' }}>{taiSanPhuDangXem.maQR}</span>
                                </div>
                            ) : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Mô tả" span={2}>{taiSanPhuDangXem.moTa || '-'}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </section>
    );
};

export default XemTaiSan;
